import React, { useState, useEffect, useCallback } from "react";
import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { PageContainer, PageHeader, Card, Table, Thead, Th, Td, TRow, Badge, Button } from "../ui/index";

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
}

const EMPTY_FORM = { email: "", password: "", first_name: "", last_name: "", role: "cashier" };

export default function EmployeePage() {
  const { user: currentUser } = useAuth();
  const isOwner = currentUser?.role === "owner";

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [resetPwId, setResetPwId] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [setPinId, setSetPinId] = useState<string | null>(null);
  const [newPin, setNewPin] = useState("");
  const [pinStatus, setPinStatus] = useState<Record<string, boolean>>({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadUsers = useCallback(async () => {
    try {
      const res = await api.get("/auth/users");
      setUsers(res.data);
      // Load PIN status for each user
      const statuses: Record<string, boolean> = {};
      for (const u of res.data) {
        try {
          const pinRes = await api.get(`/auth/users/${u.id}/has-pin`);
          statuses[u.id] = pinRes.data.has_pin;
        } catch {
          statuses[u.id] = false;
        }
      }
      setPinStatus(statuses);
    } catch {
      setError("Failed to load employees");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const clearMessages = () => { setError(""); setSuccess(""); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearMessages();

    try {
      if (editingId) {
        const updates: Record<string, string> = {};
        if (form.first_name) updates.first_name = form.first_name;
        if (form.last_name) updates.last_name = form.last_name;
        if (form.email) updates.email = form.email;
        if (form.role) updates.role = form.role;
        await api.put(`/auth/users/${editingId}`, updates);
        setSuccess("Employee updated");
      } else {
        if (!form.password) { setError("Password is required"); return; }
        await api.post("/auth/users", form);
        setSuccess("Employee created");
      }
      setShowForm(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      loadUsers();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || "Failed";
      setError(msg);
    }
  };

  const handleEdit = (u: User) => {
    setForm({ email: u.email, password: "", first_name: u.first_name, last_name: u.last_name, role: u.role });
    setEditingId(u.id);
    setShowForm(true);
    clearMessages();
  };

  const handleDeactivate = async (id: string, name: string) => {
    if (!window.confirm(`Deactivate ${name}? They will no longer be able to log in.`)) return;
    clearMessages();
    try {
      await api.delete(`/auth/users/${id}`);
      setSuccess(`${name} deactivated`);
      loadUsers();
    } catch {
      setError("Failed to deactivate");
    }
  };

  const handleReactivate = async (id: string) => {
    clearMessages();
    try {
      await api.put(`/auth/users/${id}`, { is_active: true });
      setSuccess("Employee reactivated");
      loadUsers();
    } catch {
      setError("Failed to reactivate");
    }
  };

  const handleResetPassword = async () => {
    if (!resetPwId || !newPassword) return;
    clearMessages();
    try {
      await api.post(`/auth/users/${resetPwId}/reset-password`, { new_password: newPassword });
      setSuccess("Password reset successfully");
      setResetPwId(null);
      setNewPassword("");
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || "Failed";
      setError(msg);
    }
  };

  const handleSetPin = async () => {
    if (!setPinId || !/^\d{4}$/.test(newPin)) {
      setError("PIN must be exactly 4 digits");
      return;
    }
    clearMessages();
    try {
      await api.post(`/auth/users/${setPinId}/set-pin`, { pin: newPin });
      setSuccess("PIN set successfully");
      setSetPinId(null);
      setNewPin("");
      setPinStatus((prev) => ({ ...prev, [setPinId]: true }));
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || "Failed to set PIN";
      setError(msg);
    }
  };

  const handleClearPin = async (userId: string, name: string) => {
    if (!window.confirm(`Clear PIN for ${name}? They will need a new PIN to use quick-switch.`)) return;
    clearMessages();
    try {
      await api.delete(`/auth/users/${userId}/pin`);
      setSuccess(`PIN cleared for ${name}`);
      setPinStatus((prev) => ({ ...prev, [userId]: false }));
    } catch {
      setError("Failed to clear PIN");
    }
  };

  const roleBadge = (role: string) => {
    const colors: Record<string, string> = {
      owner: "var(--rbw-gold)",
      manager: "var(--rbw-blue)",
      cashier: "var(--rbw-gray-light)",
    };
    return (
      <span className="px-2 py-0.5 rounded-full text-xs font-bold uppercase"
        style={{ background: colors[role] || "#eee", color: role === "cashier" ? "var(--rbw-navy)" : "#fff" }}>
        {role}
      </span>
    );
  };

  const activeUsers = users.filter(u => u.is_active);
  const inactiveUsers = users.filter(u => !u.is_active);

  return (
    <PageContainer>
      <PageHeader
        title="Employees"
        subtitle={`${activeUsers.length} active employee${activeUsers.length !== 1 ? "s" : ""}`}
        action={
          <Button onClick={() => { setShowForm(true); setEditingId(null); setForm(EMPTY_FORM); clearMessages(); }}>
            + Add Employee
          </Button>
        }
      />

      {error && (
        <div className="mb-4 p-3 rounded-lg text-sm font-medium" style={{ background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca" }}>
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 rounded-lg text-sm font-medium" style={{ background: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0" }}>
          {success}
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: "rgba(51,63,72,0.85)" }}>
          <div className="rounded-2xl shadow-2xl w-full max-w-md p-6" style={{ background: "var(--rbw-bg-card)" }}>
            <h2 className="text-xl font-bold mb-4" style={{ color: "var(--rbw-navy)" }}>
              {editingId ? "Edit Employee" : "Add Employee"}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold mb-1" style={{ color: "var(--rbw-gray-dark)" }}>First Name</label>
                  <input type="text" required value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--rbw-border)" }} />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1" style={{ color: "var(--rbw-gray-dark)" }}>Last Name</label>
                  <input type="text" required value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--rbw-border)" }} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1" style={{ color: "var(--rbw-gray-dark)" }}>Email</label>
                <input type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--rbw-border)" }} />
              </div>
              {!editingId && (
                <div>
                  <label className="block text-xs font-semibold mb-1" style={{ color: "var(--rbw-gray-dark)" }}>Password</label>
                  <input type="password" required value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--rbw-border)" }}
                    minLength={6} placeholder="Min 6 characters" />
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold mb-1" style={{ color: "var(--rbw-gray-dark)" }}>Role</label>
                <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: "var(--rbw-border)" }}>
                  <option value="cashier">Cashier</option>
                  {isOwner && <option value="manager">Manager</option>}
                  {isOwner && <option value="owner">Owner</option>}
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }}
                  className="flex-1 py-2 rounded-lg font-semibold text-sm" style={{ background: "var(--rbw-gray-light)", color: "var(--rbw-navy)" }}>
                  Cancel
                </button>
                <button type="submit"
                  className="flex-1 py-2 rounded-lg font-semibold text-sm" style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}>
                  {editingId ? "Save Changes" : "Create Employee"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPwId && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: "rgba(51,63,72,0.85)" }}>
          <div className="rounded-2xl shadow-2xl w-full max-w-sm p-6" style={{ background: "var(--rbw-bg-card)" }}>
            <h2 className="text-xl font-bold mb-4" style={{ color: "var(--rbw-navy)" }}>Reset Password</h2>
            <p className="text-sm mb-3" style={{ color: "var(--rbw-gray-dark)" }}>
              For: {users.find(u => u.id === resetPwId)?.first_name} {users.find(u => u.id === resetPwId)?.last_name}
            </p>
            <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border text-sm mb-4" style={{ borderColor: "var(--rbw-border)" }}
              placeholder="New password (min 6 chars)" minLength={6} autoFocus />
            <div className="flex gap-3">
              <button onClick={() => { setResetPwId(null); setNewPassword(""); }}
                className="flex-1 py-2 rounded-lg font-semibold text-sm" style={{ background: "var(--rbw-gray-light)", color: "var(--rbw-navy)" }}>
                Cancel
              </button>
              <button onClick={handleResetPassword} disabled={newPassword.length < 6}
                className="flex-1 py-2 rounded-lg font-semibold text-sm disabled:opacity-30" style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}>
                Reset
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Set PIN Modal */}
      {setPinId && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ background: "rgba(51,63,72,0.85)" }}>
          <div className="rounded-2xl shadow-2xl w-full max-w-sm p-6" style={{ background: "var(--rbw-bg-card)" }}>
            <h2 className="text-xl font-bold mb-4" style={{ color: "var(--rbw-navy)" }}>Set Register PIN</h2>
            <p className="text-sm mb-3" style={{ color: "var(--rbw-gray-dark)" }}>
              For: <span className="font-bold">{users.find(u => u.id === setPinId)?.first_name} {users.find(u => u.id === setPinId)?.last_name}</span>
            </p>
            <p className="text-xs mb-3" style={{ color: "var(--rbw-gray-dark)" }}>
              This 4-digit PIN lets them quick-switch at the register without a full login.
            </p>
            <input
              type="text"
              inputMode="numeric"
              maxLength={4}
              value={newPin}
              onChange={e => {
                const val = e.target.value.replace(/\D/g, "").slice(0, 4);
                setNewPin(val);
              }}
              className="w-full px-3 py-3 rounded-lg border text-2xl text-center tracking-[0.5em] font-bold mb-4"
              style={{ borderColor: "var(--rbw-border)", color: "var(--rbw-navy)" }}
              placeholder="0000"
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter" && newPin.length === 4) handleSetPin(); }}
            />
            <div className="flex gap-3">
              <button onClick={() => { setSetPinId(null); setNewPin(""); }}
                className="flex-1 py-2 rounded-lg font-semibold text-sm" style={{ background: "var(--rbw-gray-light)", color: "var(--rbw-navy)" }}>
                Cancel
              </button>
              <button onClick={handleSetPin} disabled={newPin.length !== 4}
                className="flex-1 py-2 rounded-lg font-semibold text-sm disabled:opacity-30" style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}>
                Set PIN
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Employee Table */}
      <Card>
        {loading ? (
          <div className="p-8 text-center" style={{ color: "var(--rbw-gray-dark)" }}>Loading...</div>
        ) : (
          <Table>
            <Thead>
              <tr>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Role</Th>
                <Th>PIN</Th>
                <Th>Last Login</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </tr>
            </Thead>
            <tbody>
              {activeUsers.map(u => (
                <TRow key={u.id}>
                  <Td><span className="font-semibold">{u.first_name} {u.last_name}</span></Td>
                  <Td>{u.email}</Td>
                  <Td>{roleBadge(u.role)}</Td>
                  <Td>
                    {pinStatus[u.id] ? (
                      <div className="flex items-center gap-1">
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: "#DCFCE7", color: "#16a34a" }}>SET</span>
                        <button onClick={() => handleClearPin(u.id, u.first_name)} className="text-xs px-1" style={{ color: "var(--rbw-gray-dark)" }}>clear</button>
                      </div>
                    ) : (
                      <button onClick={() => { setSetPinId(u.id); setNewPin(""); clearMessages(); }}
                        className="text-xs font-semibold px-2 py-0.5 rounded-full"
                        style={{ background: "var(--rbw-gray-light)", color: "var(--rbw-gray-dark)" }}>
                        Set PIN
                      </button>
                    )}
                  </Td>
                  <Td>
                    {u.last_login_at
                      ? new Date(u.last_login_at).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
                      : <span style={{ color: "var(--rbw-gray)" }}>Never</span>
                    }
                  </Td>
                  <Td><Badge variant="green">Active</Badge></Td>
                  <Td>
                    <div className="flex gap-2">
                      <button onClick={() => handleEdit(u)} className="text-xs font-semibold px-2 py-1 rounded"
                        style={{ color: "var(--rbw-navy)" }}>Edit</button>
                      <button onClick={() => { setResetPwId(u.id); clearMessages(); }} className="text-xs font-semibold px-2 py-1 rounded"
                        style={{ color: "var(--rbw-blue-dark)" }}>Reset PW</button>
                      {isOwner && u.id !== currentUser?.id && (
                        <button onClick={() => handleDeactivate(u.id, u.first_name)} className="text-xs font-semibold px-2 py-1 rounded"
                          style={{ color: "var(--rbw-danger)" }}>Deactivate</button>
                      )}
                    </div>
                  </Td>
                </TRow>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      {/* Inactive employees */}
      {inactiveUsers.length > 0 && (
        <>
          <h3 className="text-lg font-bold mt-8 mb-3" style={{ color: "var(--rbw-gray-dark)" }}>Inactive Employees</h3>
          <Card>
            <Table>
              <Thead>
                <tr><Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Actions</Th></tr>
              </Thead>
              <tbody>
                {inactiveUsers.map(u => (
                  <TRow key={u.id}>
                    <Td><span style={{ color: "var(--rbw-gray)" }}>{u.first_name} {u.last_name}</span></Td>
                    <Td><span style={{ color: "var(--rbw-gray)" }}>{u.email}</span></Td>
                    <Td>{roleBadge(u.role)}</Td>
                    <Td>
                      {isOwner && (
                        <button onClick={() => handleReactivate(u.id)} className="text-xs font-semibold px-2 py-1 rounded"
                          style={{ color: "var(--rbw-success)" }}>Reactivate</button>
                      )}
                    </Td>
                  </TRow>
                ))}
              </tbody>
            </Table>
          </Card>
        </>
      )}
    </PageContainer>
  );
}
